import * as jsonpatch from 'fast-json-patch';
import JsonPatchSrv from '@/js/services/JsonPatchSrv';
import ProjectSrv from '@/components/Projects/ProjectSrv';
import PlanningMeta from '@/models/PlanningMeta';
import PlanningConfig from '@/models/PlanningConfig';
import PlanningTimeline from '@/models/PlanningTimeline';
import PlanningLane from '@/models/PlanningLane';
import PlanningElement from '@/models/PlanningElement';
import PlanningProcessConfig from '@/models/PlanningProcessConfig';
import PlanningProcessStep from '@/models/PlanningProcessStep';
import { getDefaultProject } from '@/models/helpers/defaultValues';

class Planning {
  constructor(srcData) {
    let data;
    if (srcData instanceof Planning) {
      data = angular.copy(srcData) || {};
      _.extend(this, data);
    } else {
      if (srcData?.process?.steps?.length === 0) delete srcData.process; // old planning : reset steps with default values
      data = { ...getDefaultProject(), ...srcData }; // avoiding bad performing angular.copy(srcData). srcData is still angular.copy in each submodel
      const topLevelProps = _.omit(data, 'meta', 'config', 'timeline', 'lanes', 'elements', 'process', 'lastSavedProjectVersion'); // keep id, projectsheet, ...
      _.extend(this, angular.copy(topLevelProps));
    }

    this.meta = new PlanningMeta(data.meta);
    this.config = new PlanningConfig(data.config);
    this.timeline = new PlanningTimeline(data.timeline);

    this.lanes = [];
    data.lanes?.forEach((lane) => {
      this.lanes.push(new PlanningLane(this, lane));
    });

    this.elements = [];
    data.elements?.forEach((elData) => {
      this.elements.push(new PlanningElement(this, elData));
    });

    this.process = { config: new PlanningProcessConfig(this, data.process?.config), steps: [] };
    data.process?.steps?.forEach((step) => {
      this.process.steps.push(new PlanningProcessStep(this, step));
    });

    this.lastSavedProjectVersion = this.toProject();
    if (! (data instanceof Planning)) {
      _.extend(this.lastSavedProjectVersion.content, {
        config: data.config, timeline: data.timeline, lanes: data.lanes, elements: data.elements, process: data.process,
      });
      if (srcData && ! srcData.process?.steps) this.lastSavedProjectVersion.content.process.steps = []; // will save default process steps on next save
    }
  }

  applyContentPatch(contentPatch, updateLastSavedProject = true) {
    // updateLastSavedProject : apply content patch also to lastSavedProjectVersion, otherwise it will be detected as a modification in next save
    // Warning : function may throw an exception if contentPatch is inconsistent with planning data
    const modifications = { elements: {}, lanes: {} };
    const processStepsIdRegex = /process\/steps\/id=([^/]*)([/]?.*)/;
    const elementIdRegex = /elements\/id=([^/]*)([/]?.*)/;
    const laneIdRegex = /lanes\/id=([^/]*)([/]?.*)/;
    const jsonPatchPlanningBase = {
      config: this.config,
      timeline: this.timeline,
      process: { config: this.process.config, steps: {} },
      elements: {},
      lanes: {},
    };
    const lastSavedContent = this.lastSavedProjectVersion.content;
    const jsonPatchLastSavedContentBase = {
      config: lastSavedContent.config,
      timeline: lastSavedContent.timeline,
      process: { config: lastSavedContent.process.config, steps: {} },
      elements: {},
      lanes: {},
    };
    contentPatch.forEach((patch) => {
      if (patch.path.startsWith('/config/')) {
        if (patch.op == 'replace' && patch.path.match(/^\/config\/colors\/[0-9]+$/) && patch.value === null) return; // ignore model change {}->null
        if (patch.op == 'remove' && patch.path == '/config/icons') return; // ignore model change {}->undefined
        jsonpatch.applyOperation(jsonPatchPlanningBase, patch);
        if (updateLastSavedProject) jsonpatch.applyOperation(jsonPatchLastSavedContentBase, patch);
        modifications.config = true;
        return;
      }

      if (patch.path.startsWith('/timeline/')) {
        jsonpatch.applyOperation(jsonPatchPlanningBase, patch);
        if (updateLastSavedProject) jsonpatch.applyOperation(jsonPatchLastSavedContentBase, patch);
        modifications.timeline = true;
        return;
      }

      if (patch.path.startsWith('/elements/')) {
        const [, id, propertyPath] = elementIdRegex.exec(patch.path);
        if (! id) return;
        if (propertyPath) { // update el
          const element = this.elements.find(item => item.id == id);
          if (! element) {
            console.error('element to update not found', id, this.elements.slice(0, 3).map(item => item.id).concat(['...']));
            return;
          }
          jsonPatchPlanningBase.elements[`id=${id}`] = angular.copy(element.getAll());
          // if (patch.op == 'remove') _.extend(patch, { op: 'replace', value: null }); // keep vuejs reactivity -> BUG in arrays
          jsonpatch.applyOperation(jsonPatchPlanningBase, patch);
          element.set(jsonPatchPlanningBase.elements[`id=${id}`]);

          if (updateLastSavedProject) {
            const elementIndex = lastSavedContent.elements.findIndex(item => item.id == id);
            jsonPatchLastSavedContentBase.elements[`id=${id}`] = angular.copy(lastSavedContent.elements[elementIndex]);
            jsonpatch.applyOperation(jsonPatchLastSavedContentBase, patch);
            lastSavedContent.elements[elementIndex] = jsonPatchLastSavedContentBase.elements[`id=${id}`];
          }
        } else if (patch.op == 'remove') { // remove el
          const elementIndex = this.elements.findIndex(item => item.id == id);
          if (elementIndex > -1) this.elements.splice(elementIndex, 1);

          if (updateLastSavedProject) {
            const lastSavedContentElementIndex = lastSavedContent.elements.findIndex(item => item.id == id);
            if (lastSavedContentElementIndex > -1) lastSavedContent.elements.splice(lastSavedContentElementIndex, 1);
          }
        } else if (patch.op == 'add') { // add el
          if (this.elements.find(item => item.id == id)) return; // prevent duplicate element
          this.elements.push(new PlanningElement(this, patch.value));

          if (updateLastSavedProject) {
            lastSavedContent.elements.push((new PlanningElement(this, patch.value)).getAll());
          }
        }
        modifications.elements[id] = true;
        return;
      }

      if (patch.path.startsWith('/lanes/')) {
        const [, id, propertyPath] = laneIdRegex.exec(patch.path);
        if (! id) return;
        if (propertyPath) { // update lane
          const lane = this.lanes.find(item => item.id == id);
          jsonPatchPlanningBase.lanes[`id=${id}`] = lane.getAll();
          // if (patch.op == 'remove') _.extend(patch, { op: 'replace', value: null }); // keep vuejs reactivity -> BUG in arrays
          jsonpatch.applyOperation(jsonPatchPlanningBase, patch);
          lane.set(jsonPatchPlanningBase.lanes[`id=${id}`]);

          if (updateLastSavedProject) {
            jsonPatchLastSavedContentBase.lanes[`id=${id}`] = lastSavedContent.lanes.find(item => item.id == id);
            jsonpatch.applyOperation(jsonPatchLastSavedContentBase, patch);
          }
        } else if (patch.op == 'remove') { // remove lane
          const laneIndex = this.lanes.findIndex(item => item.id == id);
          if (laneIndex > -1) this.lanes.splice(laneIndex, 1);

          if (updateLastSavedProject) {
            const lastSavedContentLaneIndex = lastSavedContent.lanes.findIndex(item => item.id == id);
            if (lastSavedContentLaneIndex > -1) lastSavedContent.lanes.splice(lastSavedContentLaneIndex, 1);
          }
        } else if (patch.op == 'add') { // add lane
          this.lanes.push(new PlanningLane(this, patch.value));

          if (updateLastSavedProject) {
            lastSavedContent.lanes.push((new PlanningLane(this, patch.value)).getAll());
          }
        }
        modifications.lanes[id] = true;
        return;
      }

      if (patch.op == 'order' && patch.path == '/lanes') {
        this.lanes.sort((a, b) => {
          const indexA = patch.value.indexOf(a.id);
          const indexB = patch.value.indexOf(b.id);
          if (indexA == -1 || indexB == -1) return 0;
          return indexA < indexB ? -1 : 1;
        });

        if (updateLastSavedProject) {
          lastSavedContent.lanes.sort((a, b) => {
            const indexA = patch.value.indexOf(a.id);
            const indexB = patch.value.indexOf(b.id);
            if (indexA == -1 || indexB == -1) return 0;
            return indexA < indexB ? -1 : 1;
          });
        }
        return;
      }

      if (patch.path.startsWith('/process/config/')) {
        jsonpatch.applyOperation(jsonPatchPlanningBase, patch);
        if (updateLastSavedProject) jsonpatch.applyOperation(jsonPatchLastSavedContentBase, patch);
        modifications.process = true;
        return;
      }

      if (patch.path.startsWith('/process/steps/')) {
        const [, stepId, propertyPath] = processStepsIdRegex.exec(patch.path);
        if (! stepId) return;
        const id = (stepId == 'null') ? null : stepId; // id null results as string 'null'
        if (propertyPath) { // update step
          const processStep = this.process.steps.find(item => item.id == id);
          jsonPatchPlanningBase.process.steps[`id=${id}`] = processStep.getAll();
          jsonpatch.applyOperation(jsonPatchPlanningBase, patch);
          processStep.set(jsonPatchPlanningBase.process.steps[`id=${id}`]);

          if (updateLastSavedProject) {
            jsonPatchLastSavedContentBase.process.steps[`id=${id}`] = lastSavedContent.process.steps.find(item => item.id == id);
            jsonpatch.applyOperation(jsonPatchLastSavedContentBase, patch);
          }
        } else if (patch.op == 'remove') { // remove step
          const laneIndex = this.process.steps.findIndex(item => item.id == id);
          if (laneIndex > -1) this.process.steps.splice(laneIndex, 1);

          if (updateLastSavedProject) {
            const lastSavedContentProcessStepIndex = lastSavedContent.process.steps.findIndex(item => item.id == id);
            if (lastSavedContentProcessStepIndex > -1) lastSavedContent.process.steps.splice(lastSavedContentProcessStepIndex, 1);
          }
        } else if (patch.op == 'add') { // add step
          this.process.steps.push(new PlanningProcessStep(this, patch.value));

          if (updateLastSavedProject) {
            lastSavedContent.process.steps.push((new PlanningProcessStep(this, patch.value)).getAll());
          }
        }
        return;
      }

      if (patch.op == 'order' && patch.path == '/process/steps') {
        this.process.steps.sort((a, b) => {
          const indexA = patch.value.indexOf(a.id);
          const indexB = patch.value.indexOf(b.id);
          if (indexA == -1 || indexB == -1) return 0;
          return indexA < indexB ? -1 : 1;
        });

        if (updateLastSavedProject) {
          lastSavedContent.process.steps.sort((a, b) => {
            const indexA = patch.value.indexOf(a.id);
            const indexB = patch.value.indexOf(b.id);
            if (indexA == -1 || indexB == -1) return 0;
            return indexA < indexB ? -1 : 1;
          });
        }
      }
    });
    return modifications;
  }

  update(content) {
    if (! content) return {};
    if (content.id != this.id) {
      console.error(`trying to update planning ${this.id} with planning ${content.id}`);
      return {};
    }
    if (content.meta) angular.merge(this.meta, content.meta);
    const newProject = (new Planning(content)).toProject(); // ensure consistent format for lastSavedProjectVersion
    const contentPatch = this.getLastSavedVersionDiff(newProject);
    const modifications = this.applyContentPatch(contentPatch, false);

    this.lastSavedProjectVersion = newProject; // must be data from content as planning (this) can be different (eg element opened and in modification)
    return modifications;
  }

  clone() {
    return new Planning(this);
  }

  /** ******** */
  /* ACTIONS */
  /** ******** */
  toProject() {
    return {
      id: this.id,
      title: this.getTitle(),
      content: {
        config: angular.copy(this.config.getAll()),
        timeline: angular.copy(this.timeline.getAll()),
        lanes: this.lanes.map(lane => angular.copy(lane.getAll())),
        elements: this.elements.filter(el => ! el.isDragPlaceholder && ! el.isResizePlaceholder).map(el => el.getAll()),
        process: { config: angular.copy(this.process.config.getAll()), steps: this.process.steps.map(step => angular.copy(step.getAll())) },
      },
    };
  }

  getLastSavedVersionDiff(currentProject) {
    const beforeVersion = this.lastSavedProjectVersion;
    const currentVersion = currentProject || this.toProject();
    return JsonPatchSrv.getProjectDiff(beforeVersion, currentVersion);
  }

  save() {
    this.elements.forEach((el) => {
      (el.alerts || []).forEach((alert) => {
        alert.updateAndSave(el);
      });
    });

    const currentProject = this.toProject();
    if (this.workingView?.id) { // update timeline for saved working view (no id = newView)
      if (! angular.equals(this.workingView.config.timeline, currentProject.content.timeline)) {
        this.workingView.config.timeline = new PlanningTimeline(currentProject.content.timeline);
        this.workingView.save();
      }
      currentProject.content.timeline = this.lastSavedProjectVersion.content.timeline;
    }
    const contentPatch = this.getLastSavedVersionDiff(currentProject);
    if (! contentPatch.length && this.lastSavedProjectVersion.title == currentProject.title) return Promise.resolve(null);

    const save = { id: currentProject.id, title: currentProject.title, contentPatch };
    if (! this.workingView) save.content = currentProject.content;
    return ProjectSrv.save(save).then(() => {
      this.lastSavedProjectVersion = currentProject;
      return contentPatch;
    });
  }

  saveElement(el, oldState, { props, forDeletion = false } = {}) {
    // save planning with json patch only for this el ; use in dashboards with altered plannings
    const contentPatch = el.generateContentPatch(oldState, props, forDeletion);
    if (! contentPatch.length) return Promise.resolve(null);

    const currentProject = this.toProject();
    return ProjectSrv.save({ id: currentProject.id, title: currentProject.title, contentPatch }).then(() => {
      this.lastSavedProjectVersion = currentProject;
      return contentPatch;
    });
  }

  saveView(viewId) {
    const promises = [];
    const currentProject = this.toProject();
    const contentPatch = this.getLastSavedVersionDiff(currentProject);

    const elementIdRegex = /elements\/id=([^/]*)([/]?.*)/;
    contentPatch.forEach((patch) => {
      if (patch.path.startsWith('/elements/')) {
        const [, id, propertyPath] = elementIdRegex.exec(patch.path);
        if (! id) return;

        if (propertyPath) { // update el
          const element = this.elements.find(item => item.id == id);
          if (element.access_right != 'modify') return;
          const promise = element.saveView(viewId, 'update').then(() => {
            const elInCurrentProject = currentProject.content.elements.find(item => item.id == id);
            if (elInCurrentProject) _.extend(elInCurrentProject, element.getAll()); // element.saveView may update the element with the api response
          });
          promises.push(promise);
        } else if (patch.op == 'remove') { // remove el
          const element = new PlanningElement(this, this.lastSavedProjectVersion.content.elements.find(item => item.id == id));
          const promise = element.saveView(viewId, 'destroy');
          promises.push(promise);
        } else if (patch.op == 'add') { // add el
          const element = this.elements.find(item => item.id == id);
          if (element.access_right != 'modify') return;
          const promise = element.saveView(viewId, 'store').then(() => {
            const elInCurrentProject = currentProject.content.elements.find(item => item.id == id);
            if (elInCurrentProject) _.extend(elInCurrentProject, element.getAll()); // element.saveView may update the element with the api response
          });
          promises.push(promise);
        }
      }
    });

    if (! promises.length) return Promise.resolve(false);

    return Promise.all(promises).then(() => {
      this.lastSavedProjectVersion = currentProject;
      return true;
    });
  }

  saveAdmin(data) {
    return ProjectSrv.saveAdmin(_.extend(angular.copy(data), { id: this.id }));
  }

  getTitle() {
    return this.meta && this.meta.title;
  }

  getUrl() {
    const title = this.getTitle() || "New project";
    const baseUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname || '/'}`;
    return `${baseUrl}#/planning/${this.id}/${window.slugify(title)}`;
  }
}

export default Planning;
